import React, { useState, useEffect } from 'react';
import Img from 'TMComponents/ImageDynamic';
import NavLink from 'TMComponents/NavLink';
import { shape, string, number, arrayOf, bool } from 'prop-types';
import imageProps from 'TMPropTypes/imageProps';
import FlexWrapper from 'TMComponents/FlexWrapper';
import styled from 'styled-components';
import { getOneLinkURL } from 'TMComponents/AppsFlyer';
import preserveOneLinkParams from 'TMUtils/preserveOneLinkParams';

const AppstoreLinkGroupWrapper = styled(FlexWrapper)`
    max-width: 568px;
    flex-direction: row;
    gap: 20px;
    ${({ leftAlign }) =>
        leftAlign
            ? `
        margin-left: 0;
        margin-right: auto;
        justify-content: flex-start;
    `
            : `
        margin-left: auto;
        margin-right: auto;
        justify-content: center;
    `}
`;

const AppStoreLink = ({ image, link, height }) => {
    const [enhancedLink, setEnhancedLink] = useState(link);

    useEffect(() => {
        const isOneLink =
            link.includes('thinkmoney.onelink.me') ||
            link.includes('appsflyer.com');

        // OneLink detection and AppsFlyer script integration
        // istanbul ignore next - MutationObserver and window.AF_SMART_SCRIPT are browser-dependent and difficult to test in Jest environment
        if (isOneLink) {
            const observer = new MutationObserver((_, obs) => {
                /* istanbul ignore next */
                if (window.AF_SMART_SCRIPT) {
                    try {
                        const { clickURL } = getOneLinkURL();
                        const finalLink = preserveOneLinkParams(
                            link,
                            clickURL
                        );
                        setEnhancedLink(finalLink);
                    } catch (error) {
                        if (window.DD_RUM) {
                            window.DD_RUM.addError(error, {
                                context: 'AppsFlyer OneLink unavailable'
                            });
                        }
                    }
                    obs.disconnect();
                }
            });
            observer.observe(document, { childList: true, subtree: true });
            /* istanbul ignore next */
            return () => observer.disconnect();
        }
        /* istanbul ignore next */
        return undefined;
    }, [link]);

    const newImage = { ...image };
    const aspectRatio =
        newImage.gatsbyImageData.width / newImage.gatsbyImageData.height;
    newImage.gatsbyImageData.height = height;
    newImage.gatsbyImageData.width = height * aspectRatio;

    return (
        <NavLink href={enhancedLink} aria-label={newImage?.alt}>
            <Img
                imageData={newImage}
                objectFit="contain"
                loading="eager"
            />
        </NavLink>
    );
};

const AppStoreLinkGroup = ({ components, leftAlign = false }) => (
    <AppstoreLinkGroupWrapper direction="column" leftAlign={leftAlign}>
        {components.map((subItem) => (
            <AppStoreLink key={subItem.id} {...subItem} />
        ))}
    </AppstoreLinkGroupWrapper>
);

AppStoreLink.propTypes = {
    link: string.isRequired,
    image: shape(imageProps).isRequired,
    height: number
};

AppStoreLink.defaultProps = {
    height: 60
};
AppStoreLinkGroup.propTypes = {
    components: arrayOf(
        shape({
            link: string.isRequired,
            image: shape(imageProps).isRequired,
            height: number
        })
    ).isRequired,
    leftAlign: bool
};

export default AppStoreLink;
export { AppStoreLink, AppStoreLinkGroup };
